import React, { ReactElement, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import CategoryOptionsModal, {
  CategoryOption,
} from 'components/molecules/CategoryOptionsModal/CategoryOptionsModal';
import MegaMenu from 'components/molecules/MegaMenu/MegaMenu';
import Button from 'components/atoms/Button/Button';
import ChevronIcon from 'components/assets/ChevronIcon';
import clsx from 'clsx';
import classes from './CategoryDropdown.module.scss';
import Txt from 'components/atoms/Txt/Txt';

export type Props = {
  categories: CategoryOption[];
  selectedCategory?: string;
  onCategorySelect: (tag: string) => void;
  className?: string;
  listRef?: React.Ref<HTMLDivElement>;
};
function CategoryDropdown({
  categories,
  selectedCategory,
  onCategorySelect,
  className,
  listRef,
}: Readonly<Props>): ReactElement {
  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const handleToggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleCategorySelect = (tag: string) => {
    onCategorySelect(tag);
    setIsMenuOpen(false);
  };

  const handleCloseModal = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen && isDesktop) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen, isDesktop]);

  const selectedCategoryData = categories.find((cat) => cat.tag === selectedCategory);
  const allCategory = categories.find(
    (cat) => cat.tag === 'all' || cat.tag === 'allCategories' || cat.tag === '',
  );

  const effectiveCategory = selectedCategoryData ?? allCategory;
  const effectiveTag = effectiveCategory?.tag ?? '';
  const selectedCategoryName = effectiveCategory?.name ?? 'All Categories';

  const totalCount = categories.reduce((sum, cat) => sum + (cat.count ?? 0), 0);
  const displayCount = effectiveCategory?.count ?? totalCount;

  const setRefs = (node: HTMLDivElement | null) => {
    dropdownRef.current = node;
    if (!listRef) return;
    if (typeof listRef === 'function') {
      listRef(node);
    } else {
      // @ts-ignore assign to mutable ref
      (listRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  };

  return (
    <>
      <div ref={setRefs} className={clsx(classes.root, className)}>
        <Button
          buttonType="secondary"
          onClick={handleToggleMenu}
          className={classes.menuTriggerButton}
          aria-label="Open category menu"
          aria-expanded={isMenuOpen}
          aria-haspopup="true"
          icon={
            <ChevronIcon className={clsx(classes.chevron, isMenuOpen && classes.chevronOpen)} />
          }
        >
          <span>
            <Txt>{selectedCategoryName}</Txt>
            <span className={classes.categoryCount}> ({displayCount})</span>
          </span>
        </Button>

        {isDesktop && (
          <div className={clsx(classes.megaMenuWrapper, isMenuOpen && classes.megaMenuWrapperOpen)}>
            <MegaMenu
              categories={categories}
              selectedCategory={effectiveTag}
              onCategorySelect={handleCategorySelect}
            />
          </div>
        )}
      </div>

      {!isDesktop &&
        isMenuOpen &&
        createPortal(
          <CategoryOptionsModal
            categories={categories}
            selectedCategory={effectiveTag}
            onCategorySelect={handleCategorySelect}
            onClose={handleCloseModal}
          />,
          document.getElementById('modalPortal') || document.body,
        )}
    </>
  );
}

export default CategoryDropdown;
